/**
 * 备料台「画面层」—— 比例 / 风格(2026-08-06 编排页重排;2026-09-13 A4 改造)。
 *
 * ★2026-09-13 A4:曾经藏在「出成什么样」折叠里的三样(比例/时长/风格),折叠取消、常显。
 *   **时长主入口挪进「时长×拍法 联动卡」**(它与拍法/节拍/合身度联动),这里只剩比例 + 风格;
 *   仅在没有联动卡的流(蓝图学套路 / 幕二备料台 / 未选目的的自由态)才 `showDuration` 就地渲时长。
 *   风格从小控件提成独占一行的缩略图条,后跟一句「默认自由发挥最稳」取向提示。
 *
 * 取代原 `VideoBriefPanel`(「风格与格式(可选)」独立卡)。**内容一样,位置变了**:
 * 重排前这三项排在「AI 智能编排」按钮**下方 257px 处** —— 它们是喂给那颗按钮的输入,
 * 却排在它后面,用户顺着页面读到按钮就点了,引擎拿到的是默认值(诊断 P1,真机实测)。
 *
 * ★C5「卡中卡」一并修掉:原来外层是一张带边框的卡,里面风格选择器又是一张带边框的卡。
 *   现在整行是备料台的一段(无自己的边框)。
 * ★2026-08-06 用户审阅后再调:风格从"三个控件里最小的一颗"提成**独占一行的缩略图条**
 *   (`VideoStyleStrip`)—— 用户原话「风格是否可以扎眼一些,这样可以吸引用户」。
 *   随之 `VideoBriefPanel` / `VideoStylePanel` 两个组件彻底无人引用,已删。
 * ★比例写 lockedFields 的口径原样保留 —— 波3-⑦ 的定论:同一个字段两个入口
 *   (这里和高级选项)必须是同一套语义,否则 AI 编排回填会把用户改的悄悄覆盖回去。
 */
import { Dispatch, SetStateAction, useState } from "react";

import { useCreativeTaxonomy } from "../../model/useCreativeTaxonomy";

import { type FormState } from "../../model/videoCreateTypes";

import { OptionChip, OptionChipGroup } from "./OptionChip";
import { briefTargetSec } from "../../model/effectiveDuration";
import { DurationAdviceLine } from "./DurationAdviceLine";
import { VideoStyleGalleryDialog } from "./VideoStyleGalleryDialog";
import { VideoStyleStrip } from "./VideoStyleStrip";
import { VideoRatioChips } from "./VideoRatioChips";
import { useTenantIndustryCode } from "../../model/useTenantIndustryCode";

/**
 * 时长档 chip 的**单一映射**(2026-09-13 A1 起被 `VideoShootPlanCard` 复用)——
 * 时长主入口已挪进时长×拍法联动卡,那里 `import { DURATION_OPTIONS }` 用这一份,
 * 别在别处再抄一份(值漂了会两处不一致)。本行仅在 `showDuration`(蓝图学套路 / 幕二备料台 /
 * 未选目的的自由态)下仍就地渲染 —— 那些流没有联动卡,时长得有个落脚点。
 */
export const DURATION_OPTIONS = [
  { label: "短 ·15s", value: "短" },
  { label: "中 ·30s", value: "中" },
  { label: "长 ·45s", value: "长" },
  { label: "超长 ·60s", value: "超长" },
];

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  disabled?: boolean;
  /**
   * A4(2026-09-13):画面层 = 比例 + 风格。时长主入口移进「时长×拍法 联动卡」后,
   * 有联动卡的流(自由外壳选定目的)传 `false` 不再在这里渲时长(否则一字段两入口);
   * 没有联动卡的流(蓝图学套路 / 幕二备料台 / 未选目的的自由态)保持 `true`,时长仍就地渲。
   */
  showDuration?: boolean;
  /**
   * F 批(2026-09-13):比例主入口挪到「时长那一行」(目的流独立时长卡 / 自由创作单段时长行)后,
   * 那些流传 `false` 让画面层只留风格,别一字段两入口;没挪的流(蓝图/幕二/未选目的单列)保持
   * `true`,比例仍在画面层与时长并排渲(它俩本就在同一 flex 行)。
   */
  showRatio?: boolean;
}

export function VideoFormatRow({
  form,
  setForm,
  disabled,
  showDuration = true,
  showRatio = true,
}: Props) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  // 1004 第三轮:风格条 / 画廊按租户行业排(表单里带了行业码就用表单的,同调性档口径)。
  const tenantIndustryCode = useTenantIndustryCode();
  const industryCode = form.industryCode ?? tenantIndustryCode;
  const setStyle = (tone: string | null, key?: string) =>
    setForm((f) => ({ ...f, briefStyle: tone, styleCode: key ?? undefined }));
  // 时长档 → 这个档装得下多少东西(镜数/字数取自 ai 侧真在用的约束,见 shotBudget.ts)
  /**
   * 「这个档装得下多少东西」的真值:引擎节拍表的**内容位数**(0826 质感批 ⑤)。
   * taxonomy 未就绪 / 该目的没有节拍摘要 → durationAdvice 回退经验值(旧行为)。
   * 与 VideoFormulaPicker 共用同一个 queryKey,不会多打一次请求。
   */
  const taxonomyQuery = useCreativeTaxonomy(form.toneRegister);
  const targetSec = briefTargetSec(form.briefDuration);
  const contentBeats = taxonomyQuery.data?.purposes
    ?.find((p) => p.purpose === form.briefPurpose)
    ?.beatPlans?.find((b) => b.targetSec === targetSec)?.contentBeats;

  return (
    // aria-disabled 同 VideoPurposeRow:section 的隐式 role=region 不支持它,子控件已各自 disabled。
    <section
      data-video-format-row
      className={disabled ? "pointer-events-none opacity-60" : undefined}
    >
      {/* 无自己的标题:它是备料台「画面层」的一段,标题由 VideoIntakeDeck 那层给。
          这句 2026-08-02 起才成立:ai 侧 intent_infer 真读文案推目的与时长了。 */}
      <p className="mb-2 text-2xs text-muted-foreground">可选,不动就按你的文案定</p>

      {/* 比例 / 时长并排一行(它们是短选项);风格另起一行铺缩略图 —— 见 VideoStyleStrip 头注:
          风格是这页唯一能一眼看出成片长什么样的东西,不该是三个控件里最小的那个。
          窄屏(<1024)纵向堆 —— 中文 chip 横排在窄屏会挤成两行还错位。
          F 批:比例/时长都可各自关掉(挪到时长那一行了);两者全关时整行不渲染(避免空 flex 盒)。 */}
      {(showRatio || showDuration) && (
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-7">
          {showRatio && <VideoRatioChips form={form} setForm={setForm} disabled={disabled} />}

          {showDuration && (
            <div>
              <span className="mb-1.5 block text-sm text-muted-foreground">时长</span>
              <OptionChipGroup label="目标时长">
                {DURATION_OPTIONS.map((d) => {
                  const active = form.briefDuration === d.value;
                  return (
                    <OptionChip
                      key={d.value}
                      radio
                      active={active}
                      tabIndex={active ? 0 : -1}
                      disabled={disabled}
                      onClick={() => setForm((f) => ({ ...f, briefDuration: d.value }))}
                    >
                      {d.label}
                    </OptionChip>
                  );
                })}
              </OptionChipGroup>
              {/* ★说明贴着它解释的那个控件(2026-08-06 走查):风格条插进来之后,
              这句原来落到了风格条下面,离时长隔了一整条缩略图 —— 谁也不会把它读成时长的注解。 */}
              <p className="mt-1.5 max-w-[22rem] text-2xs leading-snug text-muted-foreground/80">
                时长是目标值,AI 按它编排分镜与口播,成片可能有 3 秒左右出入。
              </p>
              {/* 这个档装得下多少(共享行,与「时长×拍法 联动卡」同一份措辞/口径)。 */}
              <DurationAdviceLine targetSec={targetSec} contentBeats={contentBeats} />
            </div>
          )}
        </div>
      )}

      <div className="mt-3">
        <span className="mb-1.5 block text-sm text-muted-foreground">风格</span>
        <VideoStyleStrip
          briefStyle={form.briefStyle ?? null}
          disabled={disabled}
          onChange={setStyle}
          onMore={() => setGalleryOpen(true)}
          industryCode={industryCode}
        />
        {/* A4:风格常显缩略图条后跟一句取向提示 —— 默认「自由发挥」最稳,选强风格反而容易跑偏。 */}
        <p
          data-testid="style-default-hint"
          className="mt-1.5 max-w-[26rem] text-2xs leading-snug text-muted-foreground/80"
        >
          默认「自由发挥」最稳:AI
          按你的文案挑合适的画风。选了某个强风格,画面容易被风格带跑、偏离产品本身。
        </p>
      </div>

      <VideoStyleGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        selectedTone={form.briefStyle ?? null}
        onSelect={setStyle}
        industryCode={industryCode}
      />
    </section>
  );
}
